import axios from 'axios';
import store from '@/store';
import { Module, VuexModule, Mutation, Action } from 'vuex-module-decorators';
import { Endpoints, Mutations, Actions } from '@/ts/store/Constants';
import ErrorResponse from '@/ts/ErrorResponse';
import { Connection } from '@/configuration/Connection';
import FeedItemData from '@/ts/FeedItemData';

@Module({name: 'Comments', namespaced: true})
export default class Comments extends VuexModule {
    public commentsDataLoaded: boolean = false;
    public comments: any = null;
    public commentsOverlayVisibility: boolean = false;

    @Mutation
    public RESET_COMMENTS_STORE_STATE(): void {
        this.commentsDataLoaded        = false;
        this.comments                  = null;
        this.commentsOverlayVisibility = false;
    }

    get getComments() {
        return this.comments;
    }

    get getCommentsOverlayVisibility(): boolean {
        return this.commentsOverlayVisibility;
    }

    get getCommentsDataLoaded(): boolean {
        return this.commentsDataLoaded;
    }

    @Action
    public FETCH_NOTIFICATION_COMMENTS(requestData: any) {
        axios.get(Endpoints.Conversations.NOTIFICATION_COMMENTS(requestData.notificationId))
            .then((response) => {
                store.commit(Mutations.SET_COMMENTS, response.data.data);
                store.dispatch(Actions.INITIALIZE_COMMENTS_SOCKET, {vue: requestData.vue, conversationId: response.data.data.id});

                for (const participant of response.data.data.participants) {
                    if (!store.getters['User/getCacheUser'](participant.userId)) {
                        store.commit(Mutations.ADD_USER_TO_CACHE, participant);
                    }
                }

                const notificationItem = store.state.Notifications.feedItems.find((item: FeedItemData) => item.id === response.data.data.notification_id);

                if (notificationItem) {
                    notificationItem.comments = response.data.data.comments.length;
                }

            })
            .catch((error) => {
                if (error.response) {
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                } else {
                    console.log(error);
                }
            });
    }

    @Action
    public ADD_COMMENT(data: any) {
        const requestData: any = {
            content: data.message,
            imageData: false,
            imageMetaData: false,
        };
        const { notificationId, eventId } = data;

        if (data.hasOwnProperty('eventId')) {
            requestData.eventId = eventId;
        } else {
            requestData.notificationId = notificationId;
        }

        return axios.post(Endpoints.Conversations.MESSAGE(data.conversationId), requestData)
            .then((response) => {
                store.commit(Mutations.ADD_NEW_COMMENT, response.data.data);
                store.commit(Mutations.INCREASE_COMMENT_COUNT, notificationId);
            })
            .catch((error) => {
                console.log(error);
            });
    }

    @Action
    public MARK_COMMENT_AS_SEEN(data: any) {
        const url = Endpoints.Conversations.MARK_CONVERSATION_AS_SEEN(
            data.conversationId,
            data.lastMessageId,
        );

        axios.put(url)
            .then(() => {
                store.commit(Mutations.MARK_COMMENTS_AS_SEEN);
            })
            .catch((error) => {
                if (error.response) {
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                } else {
                    console.log(error);
                }
            });

    }

    @Action
    public REMOVE_COMMENT(data: any) {
        const notificationId = store.getters['TenantNotifications/getActiveNotification'].id;
        return axios.delete(Endpoints.Conversations.REMOVE_MESSAGE(data.conversationId, data.id))
            .then((response) => {
                store.commit(Mutations.DELETE_COMMENT, response.data);
                store.commit(Mutations.DECREASE_COMMENT_COUNT, notificationId);
            })
            .catch((error) => {
                console.log(error);
            });
    }

    @Action
    public INITIALIZE_COMMENTS_SOCKET(socketData: any) {
        if (!Connection.socketEnabled) {
            return;
        }

        socketData.vue.$echo.private('conversation.' + socketData.conversationId)
            .listen(('.conversation.new-message'), (messagePayload: any) => {
                store.commit(Mutations.ADD_NEW_COMMENT, messagePayload);
            })
            .listen(('.conversation.message-deleted'), (messagePayload: any) => {
                store.commit(Mutations.DELETE_COMMENT, messagePayload);
            });
    }

    @Action
    public CLOSE_COMMENTS_SOCKET(socketData: any) {
        if (!Connection.socketEnabled) {
            return;
        }

        socketData.vue.$echo.leave(`conversation.${socketData.conversationId}`);
    }

    @Mutation
    public SET_COMMENTS_OVERLAY_VISIBILITY(visibility: boolean) {
        this.commentsOverlayVisibility = visibility;
        this.comments = null;
        this.commentsDataLoaded = false;
    }

    @Mutation
    public ADD_NEW_COMMENT(comment: any) {
        if (this.comments.id === comment.conversationId) {
            const messageCheck = this.comments.comments.find((commentItem: any) => commentItem.id === comment.id);

            if (!messageCheck) {
                this.comments.comments.push(comment);

                const notificationItem = store.state.Notifications.feedItems.find((item: FeedItemData) => item.id === this.comments.notification_id);

                if (notificationItem) {
                    notificationItem.comments++;
                }
            }
        }
    }

    @Mutation
    public DELETE_COMMENT(message: any) {
        if (this.comments.id === message.conversationId) {
            this.comments.comments = this.comments.comments.filter((commentItem: any) => commentItem.id !== message.messageId);

            const notificationItem = store.state.Notifications.feedItems.find((item: FeedItemData) => item.id === this.comments.notification_id);
            if (notificationItem) {
                notificationItem.comments--;
            }
        }
    }

    @Mutation
    public SET_COMMENTS(comments: boolean) {
        this.comments = comments;
        this.commentsDataLoaded = true;
    }

    @Mutation
    public RESET_COMMENTS() {
        this.comments = [];
        this.commentsDataLoaded = false;
    }

    @Mutation
    public MARK_COMMENTS_AS_SEEN() {
        this.comments.hasNewComments = 0;
    }
}
